/**
 * SUBSCRIPTION PAYWALL COMPONENT
 * ===============================
 * Production-ready paywall that displays LIVE prices from RevenueCat/Stripe.
 * 
 * Features:
 * - Fetches real prices from RevenueCat (mobile) or Stripe (web)
 * - Handles loading, success, cancellation, and error states
 * - Prevents bypass with server-side validation
 * - Updates premium status immediately after purchase
 * - Supports restore purchases (App Store requirement)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Capacitor } from '@capacitor/core';
import { Crown, Check, Loader2, AlertCircle, RefreshCw, X, Sparkles, Shield, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { purchaseService, PURCHASE_STATUS, PRODUCT_IDS } from '../services/purchaseService';
import { redirectToCheckout, isStripeAvailable, getStripeConfig } from '../services/stripeWebCheckout';
import { GetTheAppPanel } from './GetTheAppPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Check if running on native platform.
// Requires MULTIPLE positive signals to prevent false-negatives inside the AAB:
//   1) Capacitor.isNativePlatform() === true  (the primary flag set by the native bridge)
//   2) Capacitor.getPlatform() ∈ {android, ios}  (independent code path in the SDK)
// If either signal is missing, unavailable, or throws, we treat this as WEB
// and show GetTheAppPanel. Belt-and-braces so the AAB never accidentally
// shows the "Get the app" panel (native bridge is compiled into the AAB
// itself; both checks read from that bridge).
let _platformLogged = false;
const isNativeApp = () => {
  try {
    const flag = typeof Capacitor?.isNativePlatform === 'function' && Capacitor.isNativePlatform();
    const platform = typeof Capacitor?.getPlatform === 'function' ? Capacitor.getPlatform() : 'web';
    const native = flag === true && (platform === 'android' || platform === 'ios');
    if (!_platformLogged) {
      _platformLogged = true;
      // One-time QA log so you can verify detection inside the AAB
      console.log('[Paywall] Platform detection →', { flag, platform, isNative: native });
    }
    return native;
  } catch (e) {
    console.warn('[Paywall] Platform detection threw, defaulting to web:', e);
    return false;
  }
};

/**
 * Subscription Plan Card Component
 */
const PlanCard = ({ plan, isSelected, onSelect, isLoading, isBestValue }) => {
  const { t } = useTranslation();
  return (
    <button
      onClick={() => onSelect(plan.id)}
      disabled={isLoading}
      className={`relative w-full p-3 rounded-xl border-2 transition-all text-left ${
        isSelected
          ? 'border-[#C9A227] bg-gradient-to-br from-[#0F4C3A] to-[#125845] shadow-lg'
          : 'border-[#C9A227]/40 bg-white/60 hover:border-[#C9A227] hover:bg-white/80'
      } ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
      data-testid={`plan-card-${plan.id}`}
    >
      {/* Best Value Badge */}
      {isBestValue && (
        <span className="absolute -top-2 right-3 bg-gradient-to-r from-[#C9A227] to-[#B08D1F] text-[#0F4C3A] text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide shadow-sm">
          {t('paywall.bestValue', 'Best Value')}
        </span>
      )}
      
      {/* Selection Indicator */}
      <div className={`absolute top-3 left-3 w-4 h-4 rounded-full border-2 flex items-center justify-center ${
        isSelected ? 'border-[#C9A227] bg-[#C9A227]' : 'border-[#0F4C3A]/40'
      }`}>
        {isSelected && <Check className="w-2.5 h-2.5 text-[#0F4C3A]" strokeWidth={4} />}
      </div>
      
      <div className="ml-6">
        <div className="flex justify-between items-center">
          <div>
            <h3 className={`font-semibold font-serif text-sm ${isSelected ? 'text-[#F1EBDD]' : 'text-[#0F4C3A]'}`}>
              {t(`paywall.plan.${plan.id}.title`, plan.title)}
            </h3>
          </div>
          <div className="text-right">
            <p className={`text-lg font-bold leading-tight ${isSelected ? 'text-[#F1EBDD]' : 'text-[#0F4C3A]'}`}>{plan.priceDisplay}</p>
            {plan.periodDisplay && (
              <p className={`text-[10px] ${isSelected ? 'text-[#C9A227]' : 'text-[#4A4A4A]'}`}>
                {t(`paywall.plan.${plan.id}.period`, plan.periodDisplay)}
              </p>
            )}
          </div>
        </div>
        
        {plan.savings && (
          <p className={`text-[10px] mt-0.5 font-semibold ${isSelected ? 'text-[#C9A227]' : 'text-[#0F4C3A]/70'}`}>
            {plan.savings === 'Save 50%' ? t('paywall.save50', 'Save 50%')
              : plan.savings === 'Save 44%' ? t('paywall.save44', 'Save 44%')
              : plan.savings === 'Best value' ? t('paywall.bestValueLabel', 'Best value')
              : plan.savings}
          </p>
        )}
      </div>
    </button>
  );
};

/**
 * Main Subscription Paywall Component
 */
export const SubscriptionPaywall = ({ 
  onClose, 
  onSuccess,
  feature = null,  // Optional: which feature triggered the paywall
  showCloseButton = true,
  fullScreen = false,
}) => {
  // WEB LOCKOUT (2026-02-25): Subscription purchasing is disabled on web to
  // eliminate the trial-abuse vector. Web users get a "Download the app" panel
  // instead. Native path is unchanged.
  if (!isNativeApp()) {
    return (
      <GetTheAppPanel
        onClose={onClose}
        feature={feature}
        fullScreen={fullScreen}
        showCloseButton={showCloseButton}
      />
    );
  }
  return (
    <NativeSubscriptionPaywall
      onClose={onClose}
      onSuccess={onSuccess}
      feature={feature}
      showCloseButton={showCloseButton}
      fullScreen={fullScreen}
    />
  );
};

const NativeSubscriptionPaywall = ({
  onClose,
  onSuccess,
  feature = null,
  showCloseButton = true,
  fullScreen = false,
}) => {
  const { t } = useTranslation();

  // Android hardware back button + Escape always dismiss the paywall
  useEffect(() => {
    let backHandle;
    (async () => {
      if (Capacitor.isNativePlatform()) {
        try {
          const { App: CapApp } = await import('@capacitor/app');
          backHandle = await CapApp.addListener('backButton', () => onClose?.());
        } catch (e) {
          console.warn('[Paywall] back button listener unavailable:', e);
        }
      }
    })();
    const onEsc = (e) => e.key === 'Escape' && onClose?.();
    window.addEventListener('keydown', onEsc);
    return () => {
      backHandle?.remove();
      window.removeEventListener('keydown', onEsc);
    };
  }, [onClose]);
  
  // State
  const [isLoadingPlans, setIsLoadingPlans] = useState(true);
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState('yearly');
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [error, setError] = useState(null);
  const [stripeAvailable, setStripeAvailable] = useState(false);
  const [purchaseSuccess, setPurchaseSuccess] = useState(false);

  // Features list
  const proFeatures = [
    { icon: '🎤', tKey: 'paywall.feature.tajweedCoach', text: 'Tajweed Coach AI - Real-time recitation feedback' },
    { icon: '🥗', tKey: 'paywall.feature.halalScanner', text: 'Halal Scanner - AI ingredient analysis' },
    { icon: '🕌', tKey: 'paywall.feature.masjidConnect', text: 'Masjid Connect - Find & sync with local mosques' },
    { icon: '🤲', tKey: 'paywall.feature.customDua', text: 'Custom Du\'a Generator - Personalized Duas' },
    { icon: '🎙️', tKey: 'paywall.feature.reciters', text: '20+ Premium Reciters' },
    { icon: '🎨', tKey: 'paywall.feature.themes', text: 'All Premium Themes' },
    { icon: '🌙', tKey: 'paywall.feature.nightVision', text: 'Night Vision Mode' },
    { icon: '♾️', tKey: 'paywall.feature.unlimitedAI', text: 'Unlimited AI Features' },
  ];

  /**
   * Load subscription plans from RevenueCat (native) or Stripe (web)
   */
  const loadPlans = useCallback(async () => {
    setIsLoadingPlans(true);
    setError(null);
    
    try {
      if (isNativeApp()) {
        // NATIVE: Load from RevenueCat
        await purchaseService.initialize();
        const products = purchaseService.getProducts();
        
        if (products && products.length > 0) {
          // Derive canonical plan id.
          // Priority 1: subscriptionPeriod from Play Billing (deterministic, language-independent):
          //   P1M -> monthly, P1Y or P12M -> yearly, null/undefined -> lifetime.
          // Priority 2: substring match on the FULL productId (not just the base before ':',
          //   because Play Billing v5+ subscription ids look like "subscriptionId:basePlanId"
          //   and the differentiator lives on the right side of the colon).
          // Priority 3: fall back to a unique per-index id so two plans can NEVER share an id
          //   (which is what caused all three cards to appear selected simultaneously).
          const derivePlanId = (product, index) => {
            const period = (product.subscriptionPeriod || '').toUpperCase();
            if (period === 'P1M' || /^P\d+W$/.test(period)) return 'monthly';
            if (period === 'P1Y' || period === 'P12M') return 'yearly';
            if (!period) {
              // One-time / non-subscription product -> lifetime
              if ((product.productId || '').toLowerCase().includes('lifetime')) return 'lifetime';
              // Some SDKs report undefined period for lifetime; treat any non-subscription as lifetime
              return 'lifetime';
            }
            const s = (product.productId || '').toLowerCase();
            if (s.includes('monthly') || s.includes('month')) return 'monthly';
            if (s.includes('yearly') || s.includes('annual') || s.includes('year')) return 'yearly';
            if (s.includes('lifetime')) return 'lifetime';
            return `plan-${index}`;
          };
          
          const seen = new Set();
          const mappedPlans = products
            .map((product, index) => {
              const canonicalId = derivePlanId(product, index);
              // priceDisplay comes STRICTLY from RevenueCat's localized
              // priceString. If it's missing we intentionally leave the plan
              // out of the list (the offering is malformed) rather than
              // inventing a stale price. Never re-introduce a hard-coded
              // "$X.XX" fallback here — that's the pricing bug we fixed on
              // 2026-02.
              const livePrice = product.price;
              if (!livePrice) {
                console.warn('[Paywall] Skipping plan without live priceString:', product.productId);
                return null;
              }
              return {
                id: canonicalId,
                productId: product.productId,
                title: product.title || getPlanTitle(product.productId),
                priceDisplay: livePrice,
                periodDisplay: getPlanPeriod(product.productId),
                savings: canonicalId === 'yearly' ? 'Save 50%' : null,
                package: product.package,
              };
            })
            .filter(Boolean)
            .filter(p => {
              // De-duplicate by canonical id. If two products resolve to the same id,
              // keep the first and log a warning so the misconfiguration is visible.
              if (seen.has(p.id)) {
                console.warn('[Paywall] Duplicate plan id detected, skipping:', p.id, p.productId);
                return false;
              }
              seen.add(p.id);
              return true;
            });
          
          console.log('[Paywall] Loaded RevenueCat plans:', mappedPlans.map(p => ({
            id: p.id, productId: p.productId, priceDisplay: p.priceDisplay
          })));

          if (mappedPlans.length === 0) {
            // Every returned product was missing a live priceString. Show a
            // friendly error and let the user retry — never fabricate prices.
            setPlans([]);
            setError(t('paywall.errors.noValidOfferings',
              "We couldn't load live prices from the store. Please tap Retry, or try again in a moment."));
          } else {
            setPlans(mappedPlans);
          }
        } else {
          // No offerings returned from RevenueCat at all. Never fabricate
          // prices — surface a friendly retry state instead.
          console.warn('[Paywall] RevenueCat returned no products');
          setPlans([]);
          setError(t('paywall.errors.noOfferings',
            "We couldn't reach the store to load subscription prices. Please check your connection and tap Retry."));
        }
      } else {
        // WEB: Load from Stripe
        const available = await isStripeAvailable();
        setStripeAvailable(available);
        
        if (available) {
          try {
            const config = await getStripeConfig();
            
            if (config?.prices) {
              // Web/Stripe pricing must also come from live config only. If a
              // price is missing on a specific plan we skip it (rather than
              // invent "$9.99"). This matches the native-side rule.
              const buildPlan = (id, planCfg, savings) => {
                if (!planCfg?.display) return null;
                return {
                  id,
                  productId: planCfg.price_id,
                  title: id.charAt(0).toUpperCase() + id.slice(1),
                  priceDisplay: planCfg.display,
                  periodDisplay: id === 'monthly' ? 'per month'
                    : id === 'yearly' ? 'per year'
                    : 'one-time',
                  savings,
                };
              };
              const mappedPlans = [
                buildPlan('monthly',  config.prices.monthly,  null),
                buildPlan('yearly',   config.prices.yearly,   'Save 50%'),
                buildPlan('lifetime', config.prices.lifetime, 'Best value'),
              ].filter(Boolean);
              
              if (mappedPlans.length === 0) {
                setPlans([]);
                setError(t('paywall.errors.webPricesUnavailable',
                  "We couldn't load subscription prices. Please tap Retry."));
              } else {
                setPlans(mappedPlans);
                console.log('[Paywall] Loaded Stripe plans');
              }
            } else {
              setPlans([]);
              setError(t('paywall.errors.webPricesUnavailable',
                "We couldn't load subscription prices. Please tap Retry."));
            }
          } catch (e) {
            console.error('[Paywall] Failed to load Stripe config:', e);
            setPlans([]);
            setError(t('paywall.errors.webPricesUnavailable',
              "We couldn't load subscription prices. Please tap Retry."));
          }
        } else {
          // No web checkout is configured. Send the user to the native app —
          // do NOT invent prices on the web.
          setPlans([]);
          setError(t('paywall.errors.useMobileApp',
            "Subscriptions are available in the Tanzeen+ mobile app on iOS and Android."));
        }
      }
    } catch (err) {
      console.error('[Paywall] Error loading plans:', err);
      setPlans([]);
      setError(t('paywall.errors.loadFailed',
        "Unable to load subscription plans. Please tap Retry."));
    } finally {
      setIsLoadingPlans(false);
    }
  }, [t]);

  // Load plans on mount
  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  /**
   * Handle purchase button click
   */
  const handlePurchase = async () => {
    setIsPurchasing(true);
    setError(null);
    
    try {
      if (isNativeApp()) {
        // NATIVE: Use RevenueCat.
        // CRITICAL: pass the exact RevenueCat package from the tapped card, NOT a string plan.
        // This guarantees the SKU sent to Play Billing matches the card the user selected.
        const chosen = plans.find(p => p.id === selectedPlan);
        if (!chosen) {
          setError('No plan selected. Please tap a plan and try again.');
          return;
        }
        const result = await purchaseService.purchasePro({
          plan: chosen.id,
          package: chosen.package,
          productId: chosen.productId,
        });
        
        if (result.status === PURCHASE_STATUS.SUCCESS) {
          setPurchaseSuccess(true);
          toast.success(result.message);
          
          // Verify with server
          await verifyPurchaseWithServer();
          
          // Notify parent
          setTimeout(() => {
            onSuccess?.();
            onClose?.();
          }, 1500);
        } else if (result.status === PURCHASE_STATUS.CANCELLED) {
          toast.info('Purchase cancelled');
        } else {
          setError(result.message || 'Purchase failed. Please try again.');
        }
      } else {
        // WEB: Redirect to Stripe Checkout
        if (!stripeAvailable) {
          setError('Web checkout is not configured. Please use the mobile app.');
          return;
        }
        
        const userId = localStorage.getItem('quranplus_user_id');
        if (!userId) {
          setError('Please sign in to subscribe');
          return;
        }
        
        await redirectToCheckout(userId, selectedPlan);
        // User will be redirected to Stripe
      }
    } catch (err) {
      console.error('[Paywall] Purchase error:', err);
      setError(err.message || 'Purchase failed. Please try again.');
    } finally {
      setIsPurchasing(false);
    }
  };

  /**
   * Handle restore purchases.
   *
   * Three distinct outcomes must be surfaced correctly — the previous
   * version confused ERROR with "no purchases found":
   *   1. RESTORED  → subscription is active, unlock UI, close paywall
   *   2. SUCCESS   → RevenueCat responded but user has no active Pro
   *   3. ERROR     → SDK/network failure; show retry-able error
   *
   * Concurrency is guaranteed at the service layer (`_restoreInFlight`).
   * We still gate the button visually via `isRestoring`.
   */
  const handleRestore = async () => {
    if (isRestoring) return; // ignore rapid double-taps at the UI layer
    setIsRestoring(true);
    setError(null);

    try {
      const result = await purchaseService.restorePurchases();

      if (result.status === PURCHASE_STATUS.RESTORED && result.hasPro) {
        setPurchaseSuccess(true);
        toast.success(result.message || 'Purchases restored! Pro access activated.');

        // Verify with server (idempotent — the service already synced;
        // this one forces a cache invalidation so subsequent gated
        // features re-verify right away).
        await verifyPurchaseWithServer();

        setTimeout(() => {
          onSuccess?.();
          onClose?.();
        }, 1500);
      } else if (result.status === PURCHASE_STATUS.ERROR) {
        // Distinct error path — DO NOT show the "no purchases" toast here.
        const msg = result.message || 'Failed to restore purchases. Please try again.';
        setError(msg);
        toast.error(msg);
      } else {
        // PURCHASE_STATUS.SUCCESS with hasPro=false → genuinely nothing to restore.
        toast.info(result.message || 'No previous purchases found');
      }
    } catch (err) {
      // Defensive — the service should not throw, but if it does treat
      // as an error, not "no purchases".
      console.error('[Paywall] Restore error:', err);
      const msg = 'Failed to restore purchases. Please try again.';
      setError(msg);
      toast.error(msg);
    } finally {
      setIsRestoring(false);
    }
  };

  /**
   * Verify purchase with backend server
   */
  const verifyPurchaseWithServer = async () => {
    try {
      const userId = localStorage.getItem('quranplus_user_id');
      if (!userId) return;
      
      // Invalidate cache to force fresh verification
      await fetch(`${API_URL}/api/entitlements/invalidate-cache/${userId}`, {
        method: 'POST',
      });
      
      // Verify entitlement
      const response = await fetch(`${API_URL}/api/entitlements/verify/${userId}`);
      const data = await response.json();
      
      console.log('[Paywall] Server verification:', data);
      return data.is_pro;
    } catch (e) {
      console.error('[Paywall] Server verification failed:', e);
      return false;
    }
  };

  // Success state
  if (purchaseSuccess) {
    return (
      <div className={`${fullScreen ? 'min-h-screen' : ''} bg-gradient-to-b from-emerald-900 to-slate-900 flex items-center justify-center p-4`}>
        <Card className="w-full max-w-md bg-white/10 backdrop-blur-lg border-emerald-500/50">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
              <Crown className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">{t('paywall.welcomeToPro', 'Welcome to Pro!')}</h1>
            <p className="text-white/70 mb-4">
              {t('paywall.welcomeMessage', 'All premium features are now unlocked. Enjoy your enhanced experience!')}
            </p>
            <div className="flex items-center justify-center gap-2 text-emerald-400">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-sm">{t('paywall.activating', 'Activating your subscription...')}</span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div 
      className={`${fullScreen ? 'min-h-screen' : 'fixed inset-0 z-50'} bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto`}
      data-testid="subscription-paywall"
    >
      <div className="w-full max-w-md my-auto py-4 relative">
        <Card className="relative overflow-hidden border-2 border-[#C9A227] shadow-2xl bg-[#F1EBDD]">
          {/* Ornate corner flourishes */}
          <div className="absolute top-2 left-2 w-6 h-6 pointer-events-none opacity-50 z-10">
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M0 12 Q0 0, 12 0 M3 12 Q3 3, 12 3 M6 12 Q6 6, 12 6" stroke="#C9A227" strokeWidth="1" fill="none" strokeLinecap="round" />
            </svg>
          </div>
          <div className="absolute top-2 right-2 w-6 h-6 pointer-events-none opacity-50 z-10 scale-x-[-1]">
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M0 12 Q0 0, 12 0 M3 12 Q3 3, 12 3 M6 12 Q6 6, 12 6" stroke="#C9A227" strokeWidth="1" fill="none" strokeLinecap="round" />
            </svg>
          </div>

          {/* Emerald Header Ribbon */}
          <div className="bg-gradient-to-br from-[#0F4C3A] to-[#125845] px-5 py-4 text-[#F1EBDD] relative">
            {showCloseButton && (
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                className="absolute top-2 right-2 z-[60] text-[#F1EBDD]/80 hover:text-[#F1EBDD] bg-black/30 hover:bg-black/50 rounded-full w-8 h-8"
                disabled={isPurchasing || isRestoring}
                data-testid="paywall-close-btn"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </Button>
            )}
            <div className="flex flex-col items-center gap-1.5">
              <div className="p-2 bg-[#C9A227]/25 border-2 border-[#C9A227] rounded-xl shadow-inner">
                <Crown className="w-6 h-6 text-[#C9A227]" />
              </div>
              <h1 className="text-2xl font-bold font-serif text-[#F1EBDD] tracking-wide text-center leading-tight">{t('paywall.title', 'Tanzeen+ Pro')}</h1>
              <p className="text-[#C9A227] italic text-[11px] text-center leading-tight">{t('paywall.subtitle', 'Unlock the complete Islamic companion experience')}</p>
              <div className="flex items-center justify-center gap-2">
                <span className="h-px w-14 bg-gradient-to-r from-transparent to-[#C9A227]/60" />
                <Sparkles className="h-3 w-3 text-[#C9A227]" />
                <span className="h-px w-14 bg-gradient-to-l from-transparent to-[#C9A227]/60" />
              </div>
              {feature && (
                <div className="inline-block px-3 py-0.5 bg-[#C9A227]/15 border border-[#C9A227]/60 rounded-full">
                  <span className="text-[#C9A227] text-[11px]">
                    <span className="font-semibold">{feature.name}</span> {t('paywall.requiresPro', 'requires Pro')}
                  </span>
                </div>
              )}
            </div>
          </div>

          <CardContent className="p-4 bg-[#F1EBDD]">
            {/* Error Message */}
            {error && (
              <div className="mb-3 p-2.5 bg-red-500/10 border border-red-500/40 rounded-lg flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                <p className="text-red-700 text-xs">{error}</p>
              </div>
            )}

            {/* Plans Section */}
            <div className="mb-3">
              <h2 className="text-xs font-bold font-serif text-[#0F4C3A] flex items-center gap-2 mb-2 uppercase tracking-widest">
                <span className="h-px flex-1 bg-[#C9A227]/40" />
                <span className="whitespace-nowrap">{t('paywall.chooseYourPlan', 'Choose Your Plan')}</span>
                <span className="h-px flex-1 bg-[#C9A227]/40" />
              </h2>
              
              {isLoadingPlans ? (
                <div className="py-8 flex flex-col items-center">
                  <Loader2 className="w-8 h-8 text-[#0F4C3A] animate-spin mb-2" />
                  <p className="text-[#4A4A4A] text-sm">{t('paywall.loadingPrice', 'Loading price…')}</p>
                </div>
              ) : plans.length === 0 ? (
                <div className="py-6 flex flex-col items-center gap-3">
                  <AlertCircle className="w-8 h-8 text-[#0F4C3A]" />
                  <p className="text-[#4A4A4A] text-xs text-center max-w-xs">
                    {error || t('paywall.errors.loadFailed', "Unable to load subscription plans. Please tap Retry.")}
                  </p>
                  <Button
                    onClick={loadPlans}
                    disabled={isLoadingPlans}
                    variant="outline"
                    className="h-9 px-4 border-[#C9A227] text-[#0F4C3A] hover:bg-[#C9A227]/10 rounded-lg text-xs font-semibold"
                    data-testid="paywall-retry-btn"
                  >
                    <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                    {t('paywall.retry', 'Retry')}
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  {plans.map(plan => (
                    <PlanCard
                      key={plan.productId || plan.id}
                      plan={plan}
                      isSelected={selectedPlan === plan.id}
                      onSelect={setSelectedPlan}
                      isLoading={isPurchasing || isRestoring}
                      isBestValue={plan.id === 'yearly'}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Everything Included */}
            <div className="mb-3">
              <h2 className="text-xs font-bold font-serif text-[#0F4C3A] flex items-center gap-2 mb-2 uppercase tracking-widest">
                <span className="h-px flex-1 bg-[#C9A227]/40" />
                <span className="whitespace-nowrap">{t('paywall.everythingIncluded', 'Everything Included')}</span>
                <span className="h-px flex-1 bg-[#C9A227]/40" />
              </h2>
              <ul className="grid grid-cols-2 gap-x-3 gap-y-1.5">
                {proFeatures.map((feature, index) => (
                  <li key={index} className="flex items-center gap-1.5 text-[11px] text-[#0F4C3A]">
                    <span className="text-sm flex-shrink-0">{feature.icon}</span>
                    <span className="truncate">{t(feature.tKey, feature.text)}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* CTA */}
            <div className="space-y-2">
              <Button
                onClick={handlePurchase}
                disabled={isPurchasing || isRestoring || isLoadingPlans || plans.length === 0}
                className="w-full h-12 bg-gradient-to-r from-[#0F4C3A] to-[#125845] hover:from-[#125845] hover:to-[#166b4f] text-[#F1EBDD] font-bold text-base rounded-xl border-2 border-[#C9A227] shadow-lg disabled:opacity-60"
                data-testid="purchase-button"
              >
                {isPurchasing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('paywall.processing', 'Processing...')}
                  </>
                ) : (
                  <>
                    <Crown className="w-4 h-4 mr-2 text-[#C9A227]" />
                    {t('paywall.subscribeNow', 'Subscribe Now')}
                  </>
                )}
              </Button>

              <Button
                variant="ghost"
                onClick={handleRestore}
                disabled={isPurchasing || isRestoring}
                className="w-full h-8 text-[#0F4C3A] hover:text-[#125845] hover:bg-[#C9A227]/10 text-xs font-semibold"
              >
                {isRestoring ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                    {t('paywall.restoring', 'Restoring...')}
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                    {t('paywall.restorePurchases', 'Restore Purchases')}
                  </>
                )}
              </Button>
            </div>

            {/* Trust Badges */}
            <div className="mt-3 pt-3 border-t border-[#C9A227]/30 flex items-center justify-center gap-2 text-[#4A4A4A] text-[10px] flex-wrap">
              <span className="flex items-center gap-1">
                <Shield className="w-3 h-3" />
                {t('paywall.securePayment', 'Secure Payment')}
              </span>
              <span className="text-[#C9A227]">•</span>
              <span>{t('paywall.cancelAnytime', 'Cancel Anytime')}</span>
              <span className="text-[#C9A227]">•</span>
              <span>{isNativeApp() ? t('paywall.storeBadge', 'App Store / Play Store') : t('paywall.stripeBadge', 'Powered by Stripe')}</span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

/**
 * Helper functions
 */
function getPlanTitle(productId) {
  if (productId.includes('monthly')) return 'Monthly';
  if (productId.includes('yearly')) return 'Yearly';
  if (productId.includes('lifetime')) return 'Lifetime';
  return 'Subscription';
}

// NOTE (2026-02): The old `getDefaultPlans()` and `getPlanFallbackPrice()`
// helpers used to return hard-coded '$2.99' / '$19.99' / '$49.99' prices
// whenever RevenueCat wasn't reachable. They've been deliberately removed.
// The paywall now always renders `pkg.product.priceString` returned by
// RevenueCat, and shows a friendly loading / retry state when live prices
// are unavailable. Do NOT re-introduce hard-coded price strings here.

function getPlanPeriod(productId) {
  if (productId.includes('monthly')) return 'per month';
  if (productId.includes('yearly')) return 'per year';
  if (productId.includes('lifetime')) return 'one-time';
  return '';
}

export default SubscriptionPaywall;
